/**
 * Orders Service
 * Handles all order-related API calls
 */

import { apiService, ApiResponse } from './api.service';

/**
 * Order Status Enum
 */
export type OrderStatus = 'PENDING' | 'CONFIRMED' | 'PREPARING' | 'READY' | 'DELIVERED' | 'CLOSED' | 'CANCELLED' | 'REJECTED' | 'REFUNDED';

/**
 * Order Interface
 */
export interface Order {
  id: string;
  orderNumber: string;
  customerId: string;
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  branchId: string;
  branchName?: string;
  tenantId?: string;
  status: OrderStatus;
  paymentStatus?: string;
  orderDate?: string;
  deliveryDate?: string;
  total: number;
  subtotal: number;
  tax: number;
  discount: number;
  notes?: string;
  paymentMethod?: string;
  items?: OrderItem[];
  createdAt: string;
  updatedAt: string;
  user?: {
    id: string;
    firstName: string;
    lastName: string;
    email?: string;
  };
  customer?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
  };
}

/**
 * Order Item Interface
 */
export interface OrderItem {
  id: string;
  productId: string;
  productName?: string; // May be undefined if nested product is used
  productSku?: string;  // May be undefined if nested product is used
  quantity: number;
  price: number;
  total: number;
  discount?: number;
  notes?: string;
  product?: {
    id: string;
    name: string;
    sku: string;
    description?: string;
    price: number;
    imageUrl?: string;
  };
}

/**
 * Create Order DTO
 */
export interface CreateOrderDto {
  customerId?: string;
  tableId?: string;
  items: {
    productId: string;
    quantity: number;
    price: number;
    total: number;      // Line item total (price × quantity - item discount)
    discount?: number;  // Item-level discount
    notes?: string;
  }[];
  subtotal: number;
  tax: number;
  total: number;
  notes?: string;
  paymentMethod?: string;
  discount?: number;
}

/**
 * Update Order DTO
 */
export interface UpdateOrderDto {
  status?: OrderStatus;
  notes?: string;
  deliveryDate?: string;
}

/**
 * Orders Query Parameters
 */
export interface OrdersQuery {
  page?: number;
  limit?: number;
  status?: OrderStatus;
  customerId?: string;
  branchId?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

/**
 * Paginated Orders Response
 */
export interface PaginatedOrders {
  data: Order[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * Orders Service
 */
class OrdersService {
  /**
   * Get all orders with pagination and filters
   */
  async getOrders(query?: OrdersQuery): Promise<ApiResponse<PaginatedOrders>> {
    try {
      const params = new URLSearchParams();
      
      if (query?.page) params.append('page', query.page.toString());
      if (query?.limit) params.append('limit', query.limit.toString());
      if (query?.status) params.append('status', query.status);
      if (query?.customerId) params.append('customerId', query.customerId);
      if (query?.branchId) params.append('branchId', query.branchId);
      if (query?.startDate) params.append('startDate', query.startDate);
      if (query?.endDate) params.append('endDate', query.endDate);
      if (query?.search) params.append('search', query.search);
      if (query?.sortBy) params.append('sortBy', query.sortBy);
      if (query?.sortOrder) params.append('sortOrder', query.sortOrder);

      const queryString = params.toString() ? `?${params.toString()}` : '';
      return await apiService.get<PaginatedOrders>(`/orders${queryString}`);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      throw error;
    }
  }

  /**
   * Get single order by ID
   */
  async getOrderById(id: string): Promise<ApiResponse<Order>> {
    try {
      return await apiService.get<Order>(`/orders/${id}`);
    } catch (error) {
      console.error('Failed to fetch order:', error);
      throw error;
    }
  }

  /**
   * Create new order
   */
  async createOrder(data: CreateOrderDto): Promise<ApiResponse<Order>> {
    try {
      return await apiService.post<Order>('/orders', data);
    } catch (error) {
      console.error('Failed to create order:', error);
      throw error;
    }
  }

  /**
   * Update order
   */
  async updateOrder(id: string, data: UpdateOrderDto): Promise<ApiResponse<Order>> {
    try {
      return await apiService.patch<Order>(`/orders/${id}`, data);
    } catch (error) {
      console.error('Failed to update order:', error);
      throw error;
    }
  }

  /**
   * Delete order
   */
  async deleteOrder(id: string): Promise<ApiResponse<void>> {
    try {
      return await apiService.delete<void>(`/orders/${id}`);
    } catch (error) {
      console.error('Failed to delete order:', error);
      throw error;
    }
  }

  /**
   * Update order status
   */
  async updateOrderStatus(id: string, status: OrderStatus): Promise<ApiResponse<Order>> {
    try {
      return await apiService.patch<Order>(`/orders/${id}/status`, { status });
    } catch (error) {
      console.error('Failed to update order status:', error);
      throw error;
    }
  }

  /**
   * Get order statistics
   */
  async getOrderStats(): Promise<ApiResponse<{
    total: number;
    pending: number;
    confirmed: number;
    preparing: number;
    ready: number;
    delivered: number;
    cancelled: number;
  }>> {
    try {
      return await apiService.get('/orders/stats');
    } catch (error) {
      console.error('Failed to fetch order stats:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const ordersService = new OrdersService();